import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Chip,
  IconButton,
  TextField,
  Grid,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import {
  Print as PrintIcon,
  SelectAll as SelectAllIcon,
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon,
} from "@mui/icons-material";
import { CommonService } from "../../services/CommonService";

interface ProductDetail {
  id: number;
  product: {
    id: number;
    name: string;
    barcode: string;
    department: string;
    image_url?: string;
  };
  cost: number;
  old_price_1: number;
  new_price_1: number;
  price_difference: number;
  price_change_percentage: number;
  book?: {
    id: number;
    name: string;
    margin_1: number;
  };
}

interface UpdateDetails {
  update: {
    id: number;
    created_at: string;
    created_by: {
      id: number;
      name: string;
    };
    status: number;
  };
  details: ProductDetail[];
  summary: {
    total_products: number;
    price_increases: number;
    price_decreases: number;
    total_price_change: number;
    avg_price_change: number;
  };
}

interface SelectedProduct {
  productId: number;
  quantity: number;
}

interface PriceUpdateDetailsModalProps {
  open: boolean;
  onClose: () => void;
  updateId: number | null;
}

export const PriceUpdateDetailsModal: React.FC<PriceUpdateDetailsModalProps> = ({
  open,
  onClose,
  updateId,
}) => {
  const [details, setDetails] = useState<UpdateDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
  const [printing, setPrinting] = useState(false);

  const loadUpdateDetails = useCallback(async () => {
    if (!updateId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await CommonService.getPriceUpdateDetails(updateId);
      console.log('Price update details response:', response);

      if (response && response.success) {
        // Handle different response structures
        let detailsData = null;
        if (response.object) {
          detailsData = response.object;
        } else if (response.data) {
          detailsData = response.data;
        }

        if (detailsData) {
          setDetails(detailsData);
          // Initialize all products as selected with quantity 1
          if (detailsData.details && Array.isArray(detailsData.details)) {
            const initialSelection = detailsData.details.map((detail: ProductDetail) => ({
              productId: detail.product.id,
              quantity: 1,
            }));
            setSelectedProducts(initialSelection);
          }
        } else {
          setError('No se encontraron detalles para esta actualización');
        }
      } else {
        setError(response?.message || 'Error al cargar los detalles');
      }
    } catch (err: any) {
      console.error('Error loading update details:', err);
      if (err.message?.includes('404') || err.status === 404) {
        setError('Los detalles de esta actualización no están disponibles');
      } else {
        setError('Error de conexión al cargar los detalles');
      }
    } finally {
      setLoading(false);
    }
  }, [updateId]);

  useEffect(() => {
    if (open && updateId) {
      loadUpdateDetails();
    }
  }, [open, updateId, loadUpdateDetails]);

  const handleProductSelection = (productId: number, selected: boolean) => {
    if (selected) {
      setSelectedProducts(prev => [...prev, { productId, quantity: 1 }]);
    } else {
      setSelectedProducts(prev => prev.filter(p => p.productId !== productId));
    }
  };

  const handleQuantityChange = (productId: number, quantity: number) => {
    setSelectedProducts(prev =>
      prev.map(p => p.productId === productId ? { ...p, quantity: Math.max(1, quantity) } : p)
    );
  };

  const handleSelectAll = () => {
    if (!details || !details.details) return;

    if (selectedProducts.length === details.details.length) {
      // Deselect all
      setSelectedProducts([]);
    } else {
      // Select all
      const allSelected = details.details.map(detail => ({
        productId: detail.product.id,
        quantity: 1,
      }));
      setSelectedProducts(allSelected);
    }
  };

  const handlePrintLabels = async () => {
    if (selectedProducts.length === 0) {
      CommonService.toast('Selecciona al menos un producto para imprimir', 'warning');
      return;
    }

    setPrinting(true);

    try {
      const products = selectedProducts.map(p => ({
        id: p.productId,
        quantity: p.quantity,
      }));

      console.log("🏷️ INICIANDO IMPRESIÓN DE ETIQUETAS DE PRECIOS:");
      console.log("📦 Productos a imprimir:", products);
      console.log("📊 Total de etiquetas:", selectedProducts.reduce((sum, p) => sum + p.quantity, 0));

      await CommonService.printMultipleProductLabels(products);

      const totalLabels = selectedProducts.reduce((sum, p) => sum + p.quantity, 0);
      console.log("✅ Etiquetas enviadas exitosamente");
      CommonService.toast(`${totalLabels} etiquetas enviadas a impresión`, 'success');

      // Mark update as processed
      if (updateId) {
        console.log("📝 Marcando actualización como procesada:", updateId);
        await CommonService.markPriceUpdateAsProcessed(updateId);
      }

    } catch (err) {
      console.error('❌ Error al imprimir las etiquetas:', err);
      CommonService.toast('Error al imprimir las etiquetas', 'error');
    } finally {
      setPrinting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isProductSelected = (productId: number) => {
    return selectedProducts.some(p => p.productId === productId);
  };

  const getProductQuantity = (productId: number) => {
    return selectedProducts.find(p => p.productId === productId)?.quantity || 1;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Detalles de Actualización de Precios
            {details && ` #${details.update.id}`}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {details && (
          <Box>
            {/* Update Summary */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Fecha de Actualización
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(details.update.created_at)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Realizada por
                    </Typography>
                    <Typography variant="body1">
                      {details.update.created_by.name}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Total Productos
                    </Typography>
                    <Typography variant="h6">
                      {details.summary.total_products}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Aumentos
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      {details.summary.price_increases}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Reducciones
                    </Typography>
                    <Typography variant="h6" color="error.main">
                      {details.summary.price_decreases}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Selection Controls */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Button
                startIcon={<SelectAllIcon />}
                onClick={handleSelectAll}
                variant="outlined"
                size="small"
              >
                {selectedProducts.length === (details?.details?.length || 0) ? 'Deseleccionar Todo' : 'Seleccionar Todo'}
              </Button>
              
              <Typography variant="body2" color="textSecondary">
                {selectedProducts.length} de {details?.details?.length || 0} productos seleccionados
                {selectedProducts.length > 0 && (
                  <span> • {selectedProducts.reduce((sum, p) => sum + p.quantity, 0)} etiquetas</span>
                )}
              </Typography>
            </Box>

            {/* Products Table */}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedProducts.length === (details?.details?.length || 0)}
                        indeterminate={selectedProducts.length > 0 && selectedProducts.length < (details?.details?.length || 0)}
                        onChange={(e) => handleSelectAll()}
                      />
                    </TableCell>
                    <TableCell>Producto</TableCell>
                    <TableCell align="right">Precio Anterior</TableCell>
                    <TableCell align="right">Precio Nuevo</TableCell>
                    <TableCell align="center">Cambio</TableCell>
                    <TableCell align="center">Cantidad</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {details?.details?.map((detail) => (
                    <TableRow key={detail.id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isProductSelected(detail.product.id)}
                          onChange={(e) => handleProductSelection(detail.product.id, e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {detail.product.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {detail.product.barcode} • {detail.product.department}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        ${parseFloat(String(detail.old_price_1 || '0')).toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        ${parseFloat(String(detail.new_price_1 || '0')).toFixed(2)}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          icon={parseFloat(String(detail.price_difference || '0')) > 0 ? <TrendingUpIcon /> : parseFloat(String(detail.price_difference || '0')) < 0 ? <TrendingDownIcon /> : <RemoveIcon />}
                          label={`${parseFloat(String(detail.price_difference || '0')) > 0 ? '+' : ''}$${parseFloat(String(detail.price_difference || '0')).toFixed(2)}`}
                          color={parseFloat(String(detail.price_difference || '0')) > 0 ? 'success' : parseFloat(String(detail.price_difference || '0')) < 0 ? 'error' : 'default'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {isProductSelected(detail.product.id) && (
                          <TextField
                            type="number"
                            size="small"
                            value={getProductQuantity(detail.product.id)}
                            onChange={(e) => handleQuantityChange(detail.product.id, parseInt(e.target.value) || 1)}
                            inputProps={{ min: 1, max: 10, style: { textAlign: 'center', width: '60px' } }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  )) || []}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        <Button
          onClick={handlePrintLabels}
          variant="contained"
          startIcon={<PrintIcon />}
          disabled={printing || selectedProducts.length === 0}
        >
          {printing ? 'Imprimiendo...' : `Imprimir ${selectedProducts.reduce((sum, p) => sum + p.quantity, 0)} Etiquetas`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
